import { CONST } from '@/utils/constants'
import { URLS } from '@/utils/urls'

import Vue from 'vue'
import Router from 'vue-router'

import store from '@/store'
import * as middleware from '@/router/middleware'
import navigation from '@/utils/navigation'

// Components
import Reports from '@/components/Reports'
import IFrameComponent from '@/components/IFrameComponent'
import GridContainer from '@/components/grid/GridContainer'
import Login from '@/views/Login'
import Home from '@/views/Home'

Vue.use(Router)

const router = new Router({
  base: CONST.Services.Paths.Vue,
  mode: 'history',
  routes: [
    // H-Web 1.0: Data-Entry
    // -- Private Routes
    {
      path: '/reports',
      name: 'Reports',
      meta: {
        requiresAuth: true
      },
      component: Reports
    },
    {
      path: '/frame',
      name: 'Frame',
      component: IFrameComponent,
      meta: {
        requiresAuth: false
      },
      props: (route) => ({
        userId: route.query.userId,
        managerId: route.query.managerId
      })
    },
    {
      path: '/grid',
      name: 'Grid',
      component: GridContainer,
      meta: {
        requiresAuth: true
      },
      props: (route) => ({
        managerId: route.query.managerId
      })
    },
    // H-Web 2.0
    // -- Public Routes
    {
      path: '/login',
      name: 'Login',
      meta: {
        title: 'Log In - Hubble'
      },
      component: Login,
      beforeEnter: async (to, from, next) => {
        // Check if Logged In
        try {
          // Set Continuation Route
          let continuation = await store.dispatch('user/setContinuationRoute', to)
          // Remove if Unrecognised
          if (continuation && navigation.resolve(continuation).type === null) continuation = null
          // Get Current User Info
          await store.dispatch('user/retrieveCurrentUser')
          // Get Users Chosen StartPage
          const startPage = store.getters['user/startPage']
          // Navigate Accordingly
          navigation.go(continuation || startPage)
        } catch (err) {
          // Show Page
          next()
        }
      },
      props: (route) => ({
        urlProvidedError: (route.query.error !== undefined),
        urlProvidedTimeout: (route.query.timeout !== undefined)
      })
    },
    {
      path: '/forgot-password',
      name: 'Forgot Password',
      beforeEnter: (to, from, next) => {
        navigation.go(URLS.Public.ForgotPassword())
      }
    },
    // -- Private Routes
    {
      path: '/home',
      name: 'Home',
      component: Home,
      meta: {
        title: 'Home - Hubble',
        requiresAuth: true
      }
    },
    // -- Open Item Routes
    {
      path: '/workspace/:id([0-9]+)',
      name: 'Workspace',
      meta: {
        type: 'workspace',
        title: 'Workspace - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/visualization/:id([0-9]+)',
      name: 'Visualization',
      meta: {
        type: 'visualization',
        title: 'Visualization - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/list/:id([0-9]+)',
      name: 'List',
      meta: {
        type: 'list',
        title: 'List - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/chart/:id([0-9]+)',
      name: 'Chart',
      meta: {
        type: 'chart',
        title: 'Chart - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/metric/:id([0-9]+)',
      name: 'Metric',
      meta: {
        type: 'metric',
        title: 'Metric - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/user/:id([0-9]+)',
      name: 'User',
      meta: {
        type: 'user',
        title: 'User Profile - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/group/:id([0-9]+)',
      name: 'Group',
      meta: {
        type: 'group',
        title: 'Group - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/discussion/:id([0-9]+)',
      name: 'Discussion',
      meta: {
        type: 'discussion',
        title: 'Discussion - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/alert/:id([0-9]+)',
      name: 'Alert',
      meta: {
        type: 'alert',
        title: 'Alert - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    {
      path: '/distribution/:id([0-9]+)',
      name: 'Distribution',
      meta: {
        type: 'distribution',
        title: 'Distribution - Hubble',
        requiresAuth: true
      },
      beforeEnter: middleware.openWithGWT
    },
    // -- Catch-all Route
    {
      path: '*',
      name: 'NotFound',
      meta: {
        title: 'Hubble'
      },
      beforeEnter: (to, from, next) => {
        // Consider a 404 Page with Timer Redirect to Home
        navigation.push({ name: 'Login', query: to.query, hash: to.hash })
      }
    }
  ]
})

// Configure Middlewares
const beforeEach = (tasks => tasks.map(item => router.beforeEach(item)))([
  middleware.forceLowercaseRoutes,
  middleware.setLanguage,
  middleware.setPageTitle,
  middleware.checkAuthorization
])

export default router
